// @ts-nocheck
// Multi-book sequence — clubes com vários livros em ordem (4 em 4 meses, etc).
// Componentes desenhados no Claude Design (Clubes Grupos Prototype v2).
//
// Conteúdo:
//   SeriesPill              — pílula "LIVRO N DE M" no header do card atual
//   MarkConcluidoCTA        — link "Marcar como concluído" (admin)
//   SeriesSection           — bloco "Cronograma" (header + coverflow + manage)
//   SeriesCoverflow         — coverflow 3D com perspectiva, drag, snap
//   FlameAura               — aura/brasas sutis ao redor do livro atual
//   ConfirmAdvanceModal     — modal "Avançar pro próximo livro"
//   ManageReadingsSheet     — bottom sheet drag-to-reorder (admin)
//   useSeriesEngine         — hook local pra advance/reorder
//   SeriesAnimStyles        — keyframes globais da feature
import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '../icons';
import { BookCover } from '../common';
import { ClubSeries, ClubSeriesBook } from './data';

// ──────────────────────────────────────────────
// SeriesPill — "LIVRO N DE M"
// ──────────────────────────────────────────────
export const SeriesPill: React.FC<{ position?: number; total?: number }> = ({ position, total }) => {
  if (!total || total <= 1 || !position) return null;
  return (
    <span
      className="mono"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 8px 3px 7px',
        borderRadius: 999,
        background: 'var(--paper-3)',
        color: 'var(--ink-3)',
        fontSize: 9.5, letterSpacing: '0.16em',
        textTransform: 'uppercase', fontWeight: 600,
        animation: 'srPillFade 380ms ease-out',
      }}
    >
      <span style={{
        display: 'inline-block', width: 4, height: 4, borderRadius: '50%',
        background: 'var(--ember)',
      }}/>
      Livro {position} de {total}
    </span>
  );
};

// ──────────────────────────────────────────────
// MarkConcluidoCTA — link mono ember (admin)
// ──────────────────────────────────────────────
export const MarkConcluidoCTA: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div style={{ textAlign: 'center', marginTop: 12 }}>
    <button
      onClick={onClick}
      className="mono"
      style={{
        padding: '6px 8px',
        fontSize: 10, letterSpacing: '0.16em',
        color: 'var(--ember)',
        background: 'transparent',
        textTransform: 'uppercase',
        fontWeight: 600,
        borderBottom: '1px solid color-mix(in oklab, var(--ember) 40%, transparent)',
        borderRadius: 0,
        lineHeight: 1,
      }}
    >
      Marcar como concluído
    </button>
  </div>
);

// ──────────────────────────────────────────────
// FlameAura — aura sutil + brasas subindo (só pro livro atual)
// ──────────────────────────────────────────────
const FlameAura: React.FC<{ active?: boolean }> = ({ active = false }) => {
  const intensity = active ? 1 : 0.65;
  const particles = [
    { left: 8,  size: 3,   dur: 3.6, delay: 0.0, sway: 8,   color: 'var(--ember-2)' },
    { left: 22, size: 2.5, dur: 4.2, delay: 1.2, sway: -5,  color: 'var(--ember)' },
    { left: 38, size: 3.5, dur: 3.2, delay: 0.6, sway: 6,   color: 'var(--ember-2)' },
    { left: 56, size: 2.5, dur: 4.6, delay: 2.0, sway: -9,  color: 'var(--ember)' },
    { left: 72, size: 3,   dur: 3.8, delay: 0.4, sway: 5,   color: 'var(--ember-2)' },
    { left: 88, size: 2.5, dur: 4.0, delay: 1.6, sway: -4,  color: 'var(--ember)' },
  ];
  return (
    <>
      <div style={{
        position: 'absolute',
        inset: -10,
        borderRadius: 14,
        background:
          'radial-gradient(ellipse 70% 80% at 50% 92%, rgba(224,122,59,0.32) 0%, rgba(199,83,26,0.14) 35%, transparent 70%)',
        opacity: intensity,
        filter: 'blur(10px)',
        zIndex: -1,
        pointerEvents: 'none',
        animation: 'flameHalo 3.8s ease-in-out infinite',
      }}/>
      <div style={{
        position: 'absolute',
        inset: -3,
        borderRadius: 10,
        boxShadow: `0 0 22px 2px rgba(199,83,26,${0.18 * intensity}), 0 0 44px 8px rgba(224,122,59,${0.10 * intensity})`,
        animation: 'flameEdge 3.4s ease-in-out infinite',
        zIndex: -1,
        pointerEvents: 'none',
      }}/>
      {particles.map((p, i) => (
        <span key={i} style={{
          position: 'absolute',
          bottom: -2,
          left: `${p.left}%`,
          width: p.size, height: p.size,
          borderRadius: '50%',
          background: p.color,
          boxShadow: `0 0 ${5 + p.size * 1.5}px rgba(224,122,59,0.85), 0 0 ${10 + p.size * 2}px rgba(199,83,26,0.35)`,
          animation: `emberRise ${p.dur}s ease-out ${p.delay}s infinite both`,
          ['--ember-sway' as any]: `${p.sway}px`,
          pointerEvents: 'none',
          zIndex: 1,
          willChange: 'transform, opacity',
        }}/>
      ))}
    </>
  );
};

// ──────────────────────────────────────────────
// Coverflow 3D
// ──────────────────────────────────────────────
const CF_CARD_W = 168;
const CF_CARD_H = 252;
const CF_SPACING = 116;

interface CoverflowTransform {
  tx: number; tz: number; ry: number; sc: number;
  opacity: number; zIndex: number; abs: number;
}

const CoverflowCard: React.FC<{
  book: ClubSeriesBook;
  transform: CoverflowTransform;
  isActive: boolean;
  onClick: () => void;
}> = ({ book, transform, isActive, onClick }) => {
  const { tx, tz, ry, sc, opacity, zIndex } = transform;
  const isDone = book.state === 'done';
  const isCurrent = book.state === 'current';
  const isPlanned = book.state === 'planned';
  const isTbd = book.state === 'tbd';

  return (
    <div
      onClick={onClick}
      style={{
        position: 'absolute',
        left: '50%', top: '50%',
        marginLeft: -CF_CARD_W / 2,
        marginTop: -CF_CARD_H / 2 - 6,
        width: CF_CARD_W, height: CF_CARD_H,
        transform: `translate3d(${tx}px, 0, ${tz}px) rotateY(${ry}deg) scale(${sc})`,
        transformStyle: 'preserve-3d',
        transformOrigin: '50% 50%',
        opacity,
        zIndex,
        transition: 'transform 620ms cubic-bezier(0.22, 1, 0.36, 1), opacity 420ms ease-out, filter 420ms ease-out',
        filter: isActive ? 'none' : 'saturate(0.85) brightness(0.96)',
        cursor: 'pointer',
        willChange: 'transform, opacity',
      }}
    >
      <div style={{
        position: 'relative',
        width: '100%', height: '100%',
        borderRadius: 8,
        overflow: 'visible',
      }}>
        {isCurrent && <FlameAura active={isActive}/>}

        {isTbd ? (
          <div style={{
            width: '100%', height: '100%',
            borderRadius: 8,
            border: '1.5px dashed var(--line-strong)',
            background: 'linear-gradient(155deg, var(--paper-2) 0%, var(--paper-3) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 14, padding: 18,
            boxShadow: isActive ? '0 24px 48px -16px rgba(0,0,0,0.18)' : 'none',
          }}>
            <svg width="36" height="48" viewBox="0 0 28 38" fill="none"
              stroke="var(--ink-4)"
              strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M4 4a2 2 0 012-2h18v32H6a2 2 0 01-2-2V4z"/>
              <path d="M4 4a2 2 0 002 2h18M10 12h10M10 18h10M10 24h6" strokeDasharray="2 2"/>
            </svg>
            <div className="serif" style={{
              fontSize: 18, lineHeight: 1.15,
              fontStyle: 'italic', textAlign: 'center',
              color: 'var(--ink-3)',
            }}>A escolher</div>
            <div className="mono" style={{
              fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-4)', fontWeight: 600,
            }}>
              #{book.position}
            </div>
          </div>
        ) : (
          <div style={{
            position: 'relative',
            width: '100%', height: '100%',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: isActive
              ? '0 30px 56px -18px rgba(0,0,0,0.45), 0 8px 16px -4px rgba(0,0,0,0.20)'
              : '0 14px 28px -12px rgba(0,0,0,0.30)',
            transition: 'box-shadow 420ms',
          }}>
            <div style={{
              filter: isDone ? 'grayscale(0.78) brightness(0.92)' : 'none',
              transition: 'filter 420ms',
              width: '100%', height: '100%',
            }}>
              <BookCover book={book as any} width={CF_CARD_W} height={CF_CARD_H}/>
            </div>
            {isDone && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'color-mix(in oklab, var(--sage) 18%, transparent)',
                mixBlendMode: 'multiply',
                pointerEvents: 'none',
              }}/>
            )}
            {isCurrent && (
              <div style={{
                position: 'absolute', inset: 0,
                borderRadius: 8,
                boxShadow: 'inset 0 0 0 2px var(--ember), inset 0 0 0 4px rgba(199,83,26,0.18)',
                pointerEvents: 'none',
                animation: 'cfActiveGlow 4.2s ease-in-out infinite',
              }}/>
            )}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.10) 50%, transparent 60%)',
              pointerEvents: 'none',
            }}/>
          </div>
        )}

        {isDone && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            width: 26, height: 26, borderRadius: '50%',
            background: 'var(--sage)',
            color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 14px rgba(94,113,86,0.45)',
            zIndex: 2,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12l5 5L20 6"/>
            </svg>
          </div>
        )}

        {(isPlanned || isTbd) && !isCurrent && !isDone && (
          <div className="mono" style={{
            position: 'absolute', top: 10, left: 10,
            padding: '2px 7px', borderRadius: 4,
            background: isTbd ? 'var(--paper-3)' : 'rgba(20,16,14,0.78)',
            color: isTbd ? 'var(--ink-3)' : '#F3ECDE',
            fontSize: 9, letterSpacing: '0.10em',
            fontWeight: 700,
            zIndex: 2,
            backdropFilter: 'blur(4px)',
          }}>
            #{book.position}
          </div>
        )}

        <div style={{
          position: 'absolute',
          left: '50%', top: '100%',
          transform: 'translateX(-50%) translateY(12px)',
          width: '88%', height: 22,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 70%)',
          opacity: isActive ? 0.7 : 0.35,
          filter: 'blur(6px)',
          pointerEvents: 'none',
          transition: 'opacity 420ms',
        }}/>
      </div>
    </div>
  );
};

const CoverflowFocalDetails: React.FC<{ book?: ClubSeriesBook }> = ({ book }) => {
  if (!book) return null;
  const isDone = book.state === 'done';
  const isCurrent = book.state === 'current';
  const isPlanned = book.state === 'planned';
  const isTbd = book.state === 'tbd';

  const stateLabel = (() => {
    if (isDone) return { text: `Lido · ${book.startDate || ''}${book.endDate ? `–${book.endDate}` : ''}`, color: 'var(--sage)' };
    if (isCurrent) return { text: 'Lendo agora', color: 'var(--ember)' };
    if (isPlanned) return { text: book.startDate ? `Próximo · começa ${book.startDate}` : 'Planejado', color: 'var(--ink-2)' };
    return { text: `A escolher · livro #${book.position}`, color: 'var(--ink-3)' };
  })();

  return (
    <div
      key={book.id}
      style={{
        textAlign: 'center',
        padding: '4px 24px 0',
        opacity: 1,
        transition: 'opacity 420ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <div className="mono" style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 9.5, letterSpacing: '0.20em',
        textTransform: 'uppercase', fontWeight: 700,
        color: stateLabel.color,
      }}>
        {isCurrent && (
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: stateLabel.color,
            animation: 'srDotPulse 1.6s ease-in-out infinite',
          }}/>
        )}
        {isDone && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12l5 5L20 6"/>
          </svg>
        )}
        {stateLabel.text}
      </div>

      <div
        className="serif"
        style={{
          marginTop: 8,
          fontSize: 26, lineHeight: 1.05,
          letterSpacing: '-0.01em',
          color: isCurrent ? 'var(--ember-2)' : isTbd ? 'var(--ink-3)' : 'var(--ink)',
          fontStyle: isTbd ? 'italic' : 'normal',
          textWrap: 'balance' as any,
          maxWidth: 320,
          margin: '8px auto 0',
        }}
      >
        {book.title || 'A escolher'}
      </div>

      <div style={{
        marginTop: 4,
        fontSize: 12.5, color: 'var(--ink-3)',
        letterSpacing: '0.01em',
      }}>
        {book.author || 'Sem autor definido'}
      </div>

      {isCurrent && (
        <div style={{
          margin: '14px auto 0',
          maxWidth: 220,
          height: 3,
          borderRadius: 2,
          background: 'var(--paper-3)',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${book.collectivePct || 0}%`,
            height: '100%',
            background: 'var(--ember)',
            boxShadow: '0 0 8px rgba(199,83,26,0.4)',
            animation: 'cfProgressIn 600ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}/>
        </div>
      )}
    </div>
  );
};

const SequenceIndicator: React.FC<{
  books: ClubSeriesBook[]; active: number; onSelect: (i: number) => void;
}> = ({ books, active, onSelect }) => (
  <div style={{
    display: 'flex', gap: 4, justifyContent: 'center',
    padding: '0 20px',
  }}>
    {books.map((b, i) => {
      const isActive = i === active;
      const isDone = b.state === 'done';
      const isCurrent = b.state === 'current';
      const isTbd = b.state === 'tbd';
      const baseColor = isDone ? 'var(--sage)'
        : isCurrent ? 'var(--ember)'
        : isTbd ? 'var(--line-strong)'
        : 'var(--ink-4)';
      return (
        <button
          key={b.id}
          onClick={() => onSelect(i)}
          aria-label={`Livro ${i + 1}`}
          style={{
            flex: '0 1 36px',
            maxWidth: 36, minWidth: 16,
            height: isActive ? 4 : 2,
            borderRadius: 2,
            background: baseColor,
            opacity: isActive ? 1 : (isCurrent ? 0.85 : 0.55),
            border: isTbd ? '1px dashed var(--line-strong)' : 'none',
            padding: 0,
            transition: 'all 320ms cubic-bezier(0.22, 1, 0.36, 1)',
            cursor: 'pointer',
          }}
        />
      );
    })}
  </div>
);

const SeriesCoverflow: React.FC<{
  books: ClubSeriesBook[];
  isAdmin?: boolean;
  isAdvancing?: boolean;
  onPickTbd?: (book: ClubSeriesBook) => void;
  onAddBook?: () => void;
  onPickBook?: (book: ClubSeriesBook) => void;
}> = ({ books, isAdmin = true, isAdvancing = false, onPickTbd, onAddBook, onPickBook }) => {
  const initialActive = (() => {
    const i = books.findIndex(b => b.state === 'current');
    return i >= 0 ? i : 0;
  })();
  const [active, setActive] = useState(initialActive);
  const [dragX, setDragX] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ dragging: false, startX: 0, lastX: 0, moved: false });

  useEffect(() => { setActive(Math.min(active, books.length - 1)); }, [books.length]);

  const total = books.length;
  const liveOffset = active + (-dragX / CF_SPACING);
  const clampActive = (n: number) => Math.max(0, Math.min(total - 1, n));

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragState.current = { dragging: true, startX: e.clientX, lastX: e.clientX, moved: false };
    stageRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current.dragging) return;
    const delta = e.clientX - dragState.current.startX;
    if (Math.abs(delta) > 5) dragState.current.moved = true;
    dragState.current.lastX = e.clientX;
    setDragX(delta);
  };
  const onUp = () => {
    if (!dragState.current.dragging) return;
    dragState.current.dragging = false;
    const delta = dragState.current.lastX - dragState.current.startX;
    const indexShift = Math.round(-delta / CF_SPACING);
    setActive(clampActive(active + indexShift));
    setDragX(0);
  };

  const getTransform = (i: number): CoverflowTransform => {
    const offset = i - liveOffset;
    const abs = Math.abs(offset);
    const sign = Math.sign(offset);
    const tx = offset * CF_SPACING;
    const tz = -Math.min(abs * 110, 460);
    const ry = -sign * Math.min(abs * 28, 64);
    const sc = Math.max(0.62, 1 - abs * 0.08);
    const opacity = abs > 4.5 ? 0 : Math.max(0.12, 1 - abs * 0.22);
    const zIndex = 200 - Math.round(abs * 10);
    return { tx, tz, ry, sc, opacity, zIndex, abs };
  };

  const activeBook = books[active];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setActive(a => clampActive(a - 1));
      else if (e.key === 'ArrowRight') setActive(a => clampActive(a + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total]);

  return (
    <div style={{
      position: 'relative',
      paddingTop: 8,
      opacity: isAdvancing ? 0.55 : 1,
      transition: 'opacity 400ms',
    }}>
      <div
        ref={stageRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        style={{
          position: 'relative',
          height: 296,
          perspective: '1600px',
          perspectiveOrigin: '50% 50%',
          overflow: 'hidden',
          touchAction: 'pan-y',
          userSelect: 'none',
          cursor: dragState.current.dragging ? 'grabbing' : 'grab',
        }}
      >
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 60,
          background: 'linear-gradient(to right, var(--paper) 0%, transparent 100%)',
          zIndex: 250, pointerEvents: 'none',
        }}/>
        <div style={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: 60,
          background: 'linear-gradient(to left, var(--paper) 0%, transparent 100%)',
          zIndex: 250, pointerEvents: 'none',
        }}/>

        {books.map((book, i) => {
          const t = getTransform(i);
          if (t.opacity < 0.04) return null;
          return (
            <CoverflowCard
              key={book.id}
              book={book}
              transform={t}
              isActive={i === Math.round(liveOffset)}
              onClick={() => {
                if (dragState.current.moved) return;
                if (i === active) {
                  // Tap no card central: TBD vai pro picker; demais abrem
                  // a tela de edicao do livro (capa, paginas, etc) se admin.
                  if (book.state === 'tbd') {
                    if (isAdmin) onPickTbd?.(book);
                  } else {
                    onPickBook?.(book);
                  }
                } else {
                  setActive(i);
                }
              }}
            />
          );
        })}
      </div>

      <div style={{ marginTop: 4 }}>
        <CoverflowFocalDetails book={activeBook}/>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="mono" style={{
          textAlign: 'center',
          fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ink-3)', fontWeight: 600,
          marginBottom: 10,
        }}>
          {active + 1} <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>/</span> {total}
        </div>
        <SequenceIndicator books={books} active={active} onSelect={setActive}/>
      </div>

      {isAdmin && (
        <div style={{ padding: '20px 20px 0', textAlign: 'center' }}>
          <button
            onClick={onAddBook}
            style={{
              padding: '10px 18px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--line)',
              color: 'var(--ink-2)',
              fontSize: 12, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: 7,
              transition: 'all 160ms',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Adicionar livro à sequência
          </button>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// SeriesSection — header + coverflow + botão Gerenciar
// ──────────────────────────────────────────────
export const SeriesSection: React.FC<{
  series: ClubSeries;
  isAdmin?: boolean;
  isAdvancing?: boolean;
  onManage: () => void;
  onPickTbd?: (book: ClubSeriesBook) => void;
  onAddBook?: () => void;
  onPickBook?: (book: ClubSeriesBook) => void;
}> = ({ series, isAdmin = false, isAdvancing = false, onManage, onPickTbd, onAddBook, onPickBook }) => {
  if (!series) return null;

  const currentBook = series.books.find(b => b.state === 'current');
  const currentPos = currentBook?.position;
  const daysToNext = series.daysToNext;

  const subtitle = (() => {
    const parts: string[] = [];
    if (currentPos) {
      const ord = currentPos === 1 ? '1º' : currentPos === 2 ? '2º' : currentPos === 3 ? '3º' : `${currentPos}º`;
      parts.push(`Você está no ${ord}`);
    }
    if (daysToNext) parts.push(`${daysToNext} dias até o próximo`);
    return parts.join(' · ');
  })();

  const titleStr = (() => {
    const n = series.totalPlanned;
    const dur = series.duration;
    if (!dur) return `${n} livros`;
    return `${n} livros em ${dur.count} ${dur.unit}`;
  })();

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{
        padding: '0 20px',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="label">Cronograma</div>
          <div
            className="serif"
            style={{
              marginTop: 4,
              fontSize: 26, lineHeight: 1.05, color: 'var(--ink)',
              letterSpacing: '-0.005em',
            }}
          >
            {titleStr}
          </div>
          {subtitle && (
            <div style={{
              marginTop: 6,
              fontSize: 12, color: 'var(--ink-3)',
            }}>
              {subtitle}
            </div>
          )}
        </div>

        {isAdmin && (
          <button
            onClick={onManage}
            style={{
              flexShrink: 0,
              padding: '7px 12px 7px 11px',
              borderRadius: 999,
              border: '1px solid var(--line)',
              background: 'transparent',
              color: 'var(--ink-2)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 500,
              transition: 'all 160ms',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 4l6 6-10 10H4v-6L14 4z"/>
            </svg>
            Gerenciar
          </button>
        )}
      </div>

      <SeriesCoverflow
        books={series.books}
        isAdmin={isAdmin}
        isAdvancing={isAdvancing}
        onPickTbd={onPickTbd}
        onAddBook={onAddBook}
        onPickBook={onPickBook}
      />
    </div>
  );
};

// ──────────────────────────────────────────────
// ConfirmAdvanceModal
// ──────────────────────────────────────────────
export const ConfirmAdvanceModal: React.FC<{
  currentBook?: ClubSeriesBook;
  nextBook?: ClubSeriesBook | null;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ nextBook, onConfirm, onClose }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 90,
    background: 'rgba(10,8,6,0.55)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    animation: 'fadeIn 200ms ease-out',
  }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{
      width: '100%', maxWidth: 340,
      background: 'var(--paper-2)',
      borderRadius: 24,
      padding: 24,
      border: '1px solid var(--line)',
      boxShadow: '0 32px 64px -16px rgba(0,0,0,0.4)',
      animation: 'srModalIn 320ms cubic-bezier(0.22, 1.2, 0.36, 1)',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 14,
        background: 'var(--ember-soft)',
        color: 'var(--ember)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16,
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 5a2 2 0 012-2h10v18H6a2 2 0 01-2-2V5z"/>
          <path d="M16 12h6M19 9l3 3-3 3"/>
        </svg>
      </div>

      <div className="serif" style={{
        fontSize: 24, lineHeight: 1.1,
        letterSpacing: '-0.005em',
      }}>
        Concluir esta <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>leitura</span>?
      </div>
      {nextBook ? (
        <div style={{
          fontSize: 13, color: 'var(--ink-3)',
          lineHeight: 1.5, marginTop: 10,
        }}>
          O próximo livro do clube — <strong style={{ color: 'var(--ink)' }}>"{nextBook.title}"</strong> — vai começar hoje pra todos os membros.
        </div>
      ) : (
        <div style={{
          fontSize: 13, color: 'var(--ink-3)',
          lineHeight: 1.5, marginTop: 10,
        }}>
          Esta era a última leitura da jornada. Você pode adicionar mais livros depois.
        </div>
      )}

      {nextBook && (
        <div style={{
          marginTop: 16, padding: 12,
          borderRadius: 14,
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <BookCover book={nextBook as any} width={40} height={60}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono" style={{ fontSize: 9, letterSpacing: '0.14em', color: 'var(--ember)', textTransform: 'uppercase', fontWeight: 600 }}>
              Próximo · #{nextBook.position}
            </div>
            <div className="serif" style={{ fontSize: 16, lineHeight: 1.15, marginTop: 3 }}>{nextBook.title}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{nextBook.author}</div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button
          onClick={onConfirm}
          style={{
            width: '100%', padding: '14px',
            borderRadius: 14,
            background: 'var(--ember)', color: 'var(--paper)',
            fontSize: 14.5, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 14px 28px -10px rgba(199,83,26,0.55)',
          }}
        >
          Sim, avançar
          <Icon name="arrow-right" size={15} color="var(--paper)" stroke={2.4}/>
        </button>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '12px',
            borderRadius: 14,
            background: 'transparent',
            color: 'var(--ink-3)',
            fontSize: 13, fontWeight: 500,
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  </div>
);

// ──────────────────────────────────────────────
// ManageReadingsSheet (admin)
// ──────────────────────────────────────────────
const ManageRow: React.FC<{
  book: ClubSeriesBook;
  idx: number;
  isDragging: boolean;
  isDragTarget: boolean;
  draggingActive: boolean;
  onDragStart: (e: React.PointerEvent) => void;
  onDragEnter: () => void;
  onDragEnd: () => void;
}> = ({ book, idx, isDragging, isDragTarget, draggingActive, onDragStart, onDragEnter, onDragEnd }) => {
  const stateChip = (() => {
    if (book.state === 'done') return { label: 'Lido', bg: 'color-mix(in oklab, var(--sage) 18%, transparent)', color: 'var(--sage)' };
    if (book.state === 'current') return { label: 'Atual', bg: 'var(--ember-soft)', color: 'var(--ember)' };
    if (book.state === 'planned') return { label: 'Planejado', bg: 'var(--paper-3)', color: 'var(--ink-2)' };
    return { label: 'A escolher', bg: 'transparent', color: 'var(--ink-3)' };
  })();

  return (
    <div
      onPointerDown={onDragStart}
      onPointerEnter={onDragEnter}
      onPointerUp={onDragEnd}
      onPointerCancel={onDragEnd}
      style={{
        position: 'relative',
        padding: 12,
        borderRadius: 16,
        background: 'var(--paper)',
        border: isDragTarget ? '2px dashed var(--ember)' : '1px solid var(--line)',
        display: 'flex', alignItems: 'center', gap: 12,
        transition: 'all 220ms cubic-bezier(0.34, 1.3, 0.64, 1)',
        transform: isDragging
          ? 'scale(1.02) rotate(2deg)'
          : isDragTarget ? 'translateY(2px)' : 'none',
        boxShadow: isDragging
          ? '0 24px 48px -12px rgba(0,0,0,0.30), 0 6px 12px -4px rgba(199,83,26,0.30)'
          : 'none',
        opacity: draggingActive && !isDragging ? 0.85 : 1,
        cursor: draggingActive ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <div style={{
        flexShrink: 0,
        width: 24, height: 36,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-4)',
      }}>
        <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor">
          <circle cx="4" cy="4" r="1.5"/><circle cx="4" cy="10" r="1.5"/><circle cx="4" cy="16" r="1.5"/>
          <circle cx="10" cy="4" r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
        </svg>
      </div>

      <div style={{
        flexShrink: 0,
        width: 38, height: 50, borderRadius: 10,
        background: 'var(--ember-soft)',
        color: 'var(--ember)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Instrument Serif, serif',
        fontSize: 22, fontStyle: 'italic',
      }}>
        {idx + 1}
      </div>

      {book.state === 'tbd' ? (
        <div style={{
          flexShrink: 0,
          width: 40, height: 60, borderRadius: 4,
          border: '1.5px dashed var(--line-strong)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="14" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-4)" strokeWidth="1.5">
            <path d="M4 4a2 2 0 012-2h12v18H6a2 2 0 01-2-2V4z"/>
          </svg>
        </div>
      ) : (
        <BookCover book={book as any} width={40} height={60}/>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="serif" style={{
          fontSize: 16, lineHeight: 1.15,
          color: book.state === 'tbd' ? 'var(--ink-3)' : 'var(--ink)',
          fontStyle: book.state === 'tbd' ? 'italic' : 'normal',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {book.title || 'A escolher'}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {book.author || (book.state === 'tbd' ? 'Sem autor definido' : '')}
        </div>
        <div style={{ marginTop: 6 }}>
          <span className="mono" style={{
            display: 'inline-block',
            padding: '2px 7px', borderRadius: 999,
            background: stateChip.bg,
            color: stateChip.color,
            fontSize: 8.5, letterSpacing: '0.14em',
            textTransform: 'uppercase', fontWeight: 600,
            border: book.state === 'tbd' ? '1px dashed var(--line-strong)' : 'none',
          }}>
            {stateChip.label}
          </span>
        </div>
      </div>

      <button
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          flexShrink: 0,
          width: 32, height: 32, borderRadius: 8,
          background: 'transparent',
          color: 'var(--ink-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="more" size={16}/>
      </button>
    </div>
  );
};

export const ManageReadingsSheet: React.FC<{
  series: ClubSeries;
  onClose: () => void;
  onReorder?: (books: ClubSeriesBook[]) => void;
  onAdd: () => void;
}> = ({ series, onClose, onReorder, onAdd }) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [items, setItems] = useState<ClubSeriesBook[]>(series.books);

  useEffect(() => { setItems(series.books); }, [series]);

  const startDrag = (_e: React.PointerEvent, id: string, idx: number) => {
    setDraggingId(id);
    setHoverIdx(idx);
  };
  const onItemEnter = (idx: number) => {
    if (draggingId == null) return;
    setHoverIdx(idx);
  };
  const endDrag = () => {
    if (draggingId == null) return;
    const fromIdx = items.findIndex(x => x.id === draggingId);
    const toIdx = hoverIdx;
    if (fromIdx >= 0 && toIdx != null && fromIdx !== toIdx) {
      const next = items.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      const renumbered = next.map((b, i) => ({ ...b, position: i + 1 }));
      setItems(renumbered);
      onReorder?.(renumbered);
    }
    setDraggingId(null);
    setHoverIdx(null);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 95,
      display: 'flex', flexDirection: 'column',
      animation: 'fadeIn 180ms ease-out',
    }}>
      <div onClick={onClose} style={{
        position: 'absolute', inset: 0,
        background: 'rgba(10,8,6,0.55)',
        backdropFilter: 'blur(6px)',
      }}/>
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        height: '92%',
        background: 'var(--paper-2)',
        borderRadius: '24px 24px 0 0',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        animation: 'slideInUp 360ms cubic-bezier(0.22, 1, 0.36, 1)',
        boxShadow: '0 -16px 48px rgba(0,0,0,0.35)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'center',
          padding: '10px 0 4px',
        }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--line-strong)' }}/>
        </div>

        <div style={{
          padding: '4px 22px 14px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12,
          borderBottom: '1px solid var(--line)',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="label">Sequência de leituras</div>
            <div className="serif" style={{ fontSize: 22, marginTop: 4, lineHeight: 1.1 }}>
              Reordenar e gerenciar
            </div>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.10em', color: 'var(--ink-3)',
              marginTop: 6, textTransform: 'uppercase',
            }}>
              {series.totalPlanned} livros{series.startDate ? ` · começou ${series.startDate}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon name="close" size={16}/>
          </button>
        </div>

        <div style={{
          flex: 1, overflowY: 'auto',
          padding: '14px 16px 90px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {items.map((b, idx) => (
            <ManageRow
              key={b.id}
              book={b}
              idx={idx}
              isDragging={draggingId === b.id}
              isDragTarget={draggingId != null && draggingId !== b.id && hoverIdx === idx}
              draggingActive={draggingId != null}
              onDragStart={(e) => startDrag(e, b.id, idx)}
              onDragEnter={() => onItemEnter(idx)}
              onDragEnd={endDrag}
            />
          ))}
        </div>

        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          padding: '14px 18px 26px',
          background: 'linear-gradient(to top, var(--paper-2) 70%, rgba(0,0,0,0))',
          borderTop: '1px solid var(--line)',
        }}>
          <button
            onClick={onAdd}
            style={{
              width: '100%', padding: '15px',
              borderRadius: 16,
              background: 'var(--ink)', color: 'var(--paper)',
              fontSize: 14.5, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Adicionar livro
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// useSeriesEngine — mutation hook local (advance/reorder)
// ──────────────────────────────────────────────
export function useSeriesEngine(initialSeries?: ClubSeries | null) {
  const [series, setSeries] = useState<ClubSeries | null>(initialSeries || null);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => { setSeries(initialSeries || null); }, [initialSeries?.totalPlanned, initialSeries?.books?.length]);

  const currentBook = series?.books.find(b => b.state === 'current') || null;
  const nextBook: ClubSeriesBook | null = (() => {
    if (!series) return null;
    const curIdx = series.books.findIndex(b => b.state === 'current');
    for (let i = curIdx + 1; i < series.books.length; i++) {
      if (series.books[i].state === 'planned') return series.books[i];
    }
    return null;
  })();

  const advance = () => {
    if (!series) return;
    setAdvancing(true);
    setTimeout(() => {
      setSeries(prev => {
        if (!prev) return prev;
        const nextBooks = prev.books.map(b => {
          if (b.state === 'current') return { ...b, state: 'done' as const, collectivePct: 100, yourPct: 100, endDate: b.endDate || 'hoje' };
          if (b.id === nextBook?.id) return { ...b, state: 'current' as const, collectivePct: 0, yourPct: 0 };
          return b;
        });
        const newCurIdx = nextBooks.findIndex(b => b.state === 'current');
        return { ...prev, books: nextBooks, currentIndex: newCurIdx >= 0 ? newCurIdx : prev.currentIndex };
      });
      setAdvancing(false);
    }, 480);
  };

  const reorder = (newBooks: ClubSeriesBook[]) => {
    setSeries(prev => prev ? { ...prev, books: newBooks } : prev);
  };

  return { series, setSeries, currentBook, nextBook, advancing, advance, reorder };
}

// ──────────────────────────────────────────────
// Keyframes globais
// ──────────────────────────────────────────────
export const SeriesAnimStyles: React.FC = () => (
  <style>{`
    @keyframes srPillFade {
      0% { opacity: 0; transform: translateY(-2px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes srDotPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.75); }
    }
    @keyframes srModalIn {
      0% { opacity: 0; transform: translateY(16px) scale(0.96); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes cfActiveGlow {
      0%, 100% { box-shadow: inset 0 0 0 2px var(--ember), inset 0 0 0 4px rgba(199,83,26,0.18), 0 0 0 0 rgba(199,83,26,0); }
      50% { box-shadow: inset 0 0 0 2px var(--ember), inset 0 0 0 4px rgba(199,83,26,0.28), 0 0 28px 2px rgba(199,83,26,0.30); }
    }
    @keyframes cfProgressIn {
      0% { width: 0; }
    }
    @keyframes emberRise {
      0%   { transform: translate3d(0, 0, 0) scale(0.6); opacity: 0; }
      12%  { opacity: 0.85; }
      50%  { opacity: 0.55; }
      85%  { opacity: 0.25; }
      100% { transform: translate3d(var(--ember-sway, 4px), -120px, 0) scale(0.3); opacity: 0; }
    }
    @keyframes flameHalo {
      0%, 100% { opacity: var(--halo-opacity, 0.65); transform: scale(1); }
      50%      { opacity: calc(var(--halo-opacity, 0.65) * 1.18); transform: scale(1.04); }
    }
    @keyframes flameEdge {
      0%, 100% { filter: brightness(1); }
      50%      { filter: brightness(1.25); }
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
  `}</style>
);
